import type { FC } from 'react';
import React from 'react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl-next';

import AvatarGroup from '@atlaskit/avatar-group';
import { Stack, Text } from '@atlaskit/primitives';
import { useThemeObserver } from '@atlaskit/tokens';

import { ConfluenceEdition } from '@confluence/session-data';

import FreeUserSpot from './assets/FreeUserSpot.svg';
import FreeUserSpotDark from './assets/FreeUserSpotDark.svg';
import TakenUserSpot from './assets/TakenUserSpot.svg';
import TakenUserSpotDark from './assets/TakenUserSpotDark.svg';

type UserAvatarHeaderProps = {
	edition: ConfluenceEdition | null;
	text: MessageDescriptor;
	userCount: number;
};

type UserAvatarSubHeaderProps = {
	edition: ConfluenceEdition | null;
	text: MessageDescriptor;
	userCount: number;
};

type UserAvatarDataProps = {
	name: string;
	src?: string;
	userId?: string;
	href?: string;
}[];

type UserAvatarProps = {
	edition: ConfluenceEdition | null;
	userCount: number;
	userAvatarData: UserAvatarDataProps;
};

type UserAvatarSectionProps = {
	edition: ConfluenceEdition | null;
	text: { [index: string]: MessageDescriptor };
	userCount: number;
	userAvatarData: UserAvatarDataProps;
};

const addFreeUserAvatars = (userCount: number, userAvatarData: UserAvatarDataProps, src: any) => {
	const freeUserAvatar = { name: 'Free User Spot', src };

	if (userCount > 10) {
		return userAvatarData;
	}
	return userAvatarData.concat(new Array(10 - userCount).fill(freeUserAvatar));
};

const getStaticUserAvatars = (
	userCount: number,
	edition: ConfluenceEdition | null,
	freeUserSpotSrc: any,
	takenUserSpotSrc: any,
) => {
	const takenUserAvatar = { name: 'Taken User Spot', src: takenUserSpotSrc };
	const userAvatarData = new Array(userCount).fill(takenUserAvatar);

	if (userCount < 11 && edition === ConfluenceEdition.FREE) {
		const freeUserAvatar = { name: 'Free User Spot', src: freeUserSpotSrc };
		return userAvatarData.concat(new Array(10 - userCount).fill(freeUserAvatar));
	}

	return userAvatarData;
};

const UserAvatarSectionHeader: FC<UserAvatarHeaderProps> = ({ edition, text, userCount }) => {
	return (
		<Text color="color.text" size="large" weight="medium">
			<FormattedMessage
				{...text}
				values={edition === ConfluenceEdition.FREE ? { userCount } : {}}
			/>
		</Text>
	);
};

const UserAvatarSectionSubHeader: FC<UserAvatarSubHeaderProps> = ({ edition, text, userCount }) => {
	const userLimitOverFreeLimit = userCount - 10;

	return (
		<Text color="color.text.subtlest" size="small">
			{edition === ConfluenceEdition.FREE ? (
				<FormattedMessage {...text} />
			) : (
				<FormattedMessage {...text} values={userCount > 10 ? { userLimitOverFreeLimit } : {}} />
			)}
		</Text>
	);
};

const UserAvatars = ({ edition, userCount, userAvatarData }: UserAvatarProps) => {
	const { colorMode } = useThemeObserver();
	const freeUserAvatarSrc = colorMode === 'dark' ? FreeUserSpotDark : FreeUserSpot;
	const takenUserSpotSrc = colorMode === 'dark' ? TakenUserSpotDark : TakenUserSpot;

	return (
		<AvatarGroup
			testId="user-avatar-section-avatar-group"
			appearance="stack"
			data={
				userCount !== userAvatarData.length
					? getStaticUserAvatars(userCount, edition, freeUserAvatarSrc, takenUserSpotSrc)
					: edition === ConfluenceEdition.FREE
						? addFreeUserAvatars(userCount, userAvatarData, freeUserAvatarSrc)
						: userAvatarData
			}
			maxCount={11}
			size="large"
		/>
	);
};

export const UserAvatarSection: FC<UserAvatarSectionProps> = ({
	edition,
	text,
	userCount,
	userAvatarData,
}) => {
	return (
		<Stack testId="user-avatar-section" space="space.250">
			<Stack space="space.050">
				<UserAvatarSectionHeader edition={edition} userCount={userCount} text={text.header} />
				<UserAvatarSectionSubHeader edition={edition} userCount={userCount} text={text.subHeader} />
			</Stack>
			<UserAvatars edition={edition} userCount={userCount} userAvatarData={userAvatarData} />
		</Stack>
	);
};
